import type { TFunction } from 'i18next';
import type { SharedViewFailureDetails, SharedViewFailureReason } from '@/utils/filter/sharedViewDiagnostics';

export interface SharedViewDetailRow {
  id: string;
  label: string;
  values: readonly string[];
  isList?: boolean;
}

const PREFIX = 'global.filters.sharedViewDialog.errorDetails';

const joinKeys = (keys: string[]): string => keys.join(', ');

const buildDiagnosisText = (details: SharedViewFailureDetails, t: TFunction): string => {
  const { diagnosis } = details;
  const notAvailable = t(`${PREFIX}.notAvailable`);
  return t(`${PREFIX}.diagnoses.${diagnosis.kind}`, {
    httpStatus: diagnosis.httpStatus ?? notAvailable,
    errorCode: diagnosis.errorCode ?? notAvailable,
    interpolation: { escapeValue: false },
  });
};

const buildProgressValue = (details: SharedViewFailureDetails, t: TFunction): string => {
  const completed = details.stagesCompleted.map(
    completedStage => `${t(`${PREFIX}.stages.${completedStage}`)}: ${t(`${PREFIX}.progressOk`)}`,
  );
  const failed = `${t(`${PREFIX}.stages.${details.stage}`)}: ${t(`${PREFIX}.progressFailed`)}`;
  return [...completed, failed].join(' → ');
};

const buildReasonText = (reason: SharedViewFailureReason, t: TFunction): string =>
  t(`${PREFIX}.reason.${reason.kind}`, {
    field: reason.field,
    keys: reason.keys?.join(', '),
    actualType: reason.actualType,
    statusCode: reason.statusCode,
    interpolation: { escapeValue: false },
  });

const buildResponseRows = (details: SharedViewFailureDetails, t: TFunction): SharedViewDetailRow[] => {
  const { response } = details;
  if (!response) {
    return [];
  }
  const rows: SharedViewDetailRow[] = [];
  if (response.statusLabel !== undefined) {
    rows.push({ id: 'responseStatusLabel', label: t(`${PREFIX}.responseStatusLabel`), values: [response.statusLabel] });
  }
  response.records.forEach(record => {
    const valueKey = record.filterType === undefined ? 'recordValue' : 'recordValueWithType';
    rows.push({
      id: `record-${record.key}`,
      label: t(`${PREFIX}.record`),
      values: [
        t(`${PREFIX}.${valueKey}`, {
          key: record.key,
          filterType: record.filterType,
          keys: joinKeys(record.keys),
          interpolation: { escapeValue: false },
        }),
      ],
    });
  });
  if (response.filterCountriesType !== undefined) {
    rows.push({ id: 'filterCountries', label: t(`${PREFIX}.filterCountries`), values: [response.filterCountriesType] });
  }
  return rows;
};

const buildPayloadRows = (details: SharedViewFailureDetails, t: TFunction): SharedViewDetailRow[] => {
  const { payloadSummary } = details;
  if (!payloadSummary) {
    return [];
  }
  const rows: SharedViewDetailRow[] = [];
  if (payloadSummary.envelopeKeys) {
    rows.push({
      id: 'envelopeKeys',
      label: t(`${PREFIX}.payloadEnvelopeKeys`),
      values: [joinKeys(payloadSummary.envelopeKeys)],
    });
  }
  rows.push({ id: 'payloadKeys', label: t(`${PREFIX}.payloadKeys`), values: [joinKeys(payloadSummary.payloadKeys)] });
  if (payloadSummary.globalFilterKeys) {
    rows.push({
      id: 'globalFilterKeys',
      label: t(`${PREFIX}.globalFilterKeys`),
      values: [
        t(`${PREFIX}.globalFilterKeysValue`, {
          keys: joinKeys(payloadSummary.globalFilterKeys),
          count: payloadSummary.globalFilterKeys.length,
          interpolation: { escapeValue: false },
        }),
      ],
    });
  }
  payloadSummary.optionalFields.forEach(field => {
    rows.push({
      id: `field-${field.name}`,
      label: t(`${PREFIX}.optionalField`, { name: field.name, interpolation: { escapeValue: false } }),
      values: [field.type ?? t(`${PREFIX}.absent`)],
    });
  });
  return rows;
};

export const buildSharedViewDetailRows = (details: SharedViewFailureDetails, t: TFunction): SharedViewDetailRow[] => {
  const optionalRows: [string, string, string | number | undefined][] = [
    ['requestInfo', t(`${PREFIX}.request`), details.requestInfo && t(`${PREFIX}.requests.${details.requestInfo}`)],
    ['httpStatus', t(`${PREFIX}.httpStatus`), details.httpStatus],
    ['errorCode', t(`${PREFIX}.errorCode`), details.errorCode],
    ['errorName', t(`${PREFIX}.errorName`), details.errorName],
    ['shareViewId', t(`${PREFIX}.shareViewId`), details.shareViewId],
    ['requestId', t(`${PREFIX}.requestId`), details.requestId],
    ['occurredAt', t(`${PREFIX}.occurredAt`), details.occurredAt],
    ['appHost', t(`${PREFIX}.appHost`), details.appHost],
    ['apiHost', t(`${PREFIX}.apiHost`), details.apiHost],
  ];
  const simpleRows = optionalRows.flatMap(([id, label, value]) =>
    value === undefined || value === '' ? [] : [{ id, label, values: [String(value)] }],
  );
  const reasonsRow: SharedViewDetailRow[] =
    details.reasons.length > 0
      ? [
          {
            id: 'reasons',
            label: t(`${PREFIX}.reasons`),
            values: details.reasons.map(reason => buildReasonText(reason, t)),
            isList: true,
          },
        ]
      : [];

  return [
    { id: 'diagnosis', label: t(`${PREFIX}.diagnosis`), values: [buildDiagnosisText(details, t)] },
    { id: 'stage', label: t(`${PREFIX}.stage`), values: [t(`${PREFIX}.stages.${details.stage}`)] },
    { id: 'progress', label: t(`${PREFIX}.progress`), values: [buildProgressValue(details, t)] },
    ...simpleRows,
    ...buildResponseRows(details, t),
    ...buildPayloadRows(details, t),
    ...reasonsRow,
  ];
};

export const formatSharedViewDetailsText = (rows: SharedViewDetailRow[]): string =>
  rows
    .map(detailRow =>
      detailRow.isList
        ? [`${detailRow.label}:`, ...detailRow.values.map(valueText => `- ${valueText}`)].join('\n')
        : `${detailRow.label}: ${detailRow.values.join('; ')}`,
    )
    .join('\n');
